import * as React from 'react'

import * as RadioGroup from '@radix-ui/react-radio-group'
import { clsx } from 'clsx'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'
import {
  BlockChange,
  BooleanBlock,
  hasVariantOverrides,
} from '@basehub/data/replicache/index'

type BooleanDiffData = {
  overrideKey: string
  value: boolean
  valueChanged: boolean
  className?: string
}

export const BooleanBlockDiff = ({
  change,
  condition,
}: {
  change: BlockChange
  condition: 'removed' | 'added'
}) => {
  const valueChanged = change.previous?.value !== change.current?.value

  const isDeletedAndConditionIsAdded =
    condition === 'added' && change.status === 'deleted'

  const overrides: BooleanDiffData[] = React.useMemo(() => {
    const source = condition === 'added' ? change.current : change.previous
    if (!source || !hasVariantOverrides(source)) {
      return []
    } else {
      const different = (
        condition === 'added' ? change.previous : change.current
      ) as BooleanBlock | null
      return Object.entries(source.variantOverrides ?? {}).map(
        ([overrideKey, override]) => {
          const previousOverride = different?.variantOverrides?.[overrideKey]
            ?.value as boolean | undefined
          const valueChanged = override.value !== previousOverride
          return {
            overrideKey,
            valueChanged,
            value: override.value,
          }
        }
      )
    }
  }, [change, condition])

  const Children = React.useCallback(
    (props: BooleanDiffData) => (
      <PrimitiveBooleanBlockDiff
        blockValue={props.value}
        disabled
        className={clsx(
          isDeletedAndConditionIsAdded && 'opacity-30',
          props.className
        )}
        condition={props.valueChanged && condition}
      />
    ),
    [condition, isDeletedAndConditionIsAdded]
  )

  return (
    <>
      <PrimitiveBooleanBlockDiff
        blockValue={
          condition === 'added'
            ? (change.current?.value as boolean)
            : ((change.conflicting?.value ?? change.previous?.value) as boolean)
        }
        disabled
        className={clsx(isDeletedAndConditionIsAdded && 'opacity-30')}
        condition={valueChanged && condition}
      />
      {overrides.length ? (
        <OverridesDiffWrapper<BooleanDiffData> overrides={overrides}>
          {Children}
        </OverridesDiffWrapper>
      ) : null}
    </>
  )
}

const PrimitiveBooleanBlockDiff = ({
  blockValue,
  condition,
  className,
  ...props
}: {
  condition: 'removed' | 'added' | false
  blockValue: boolean
} & RadioGroup.RadioGroupProps) => {
  const r1 = React.useId()
  const r2 = React.useId()

  const classes = React.useMemo(
    () => ({
      itemContainer: (active: boolean) =>
        clsx('group flex items-center gap-2', !active && 'opacity-30'),
      item: (active: boolean) =>
        clsx(
          'flex size-4 cursor-default items-center justify-center rounded-full border border-dark-elevated bg-dark-border radix-state-checked:border-dark-control-base',
          active && 'bg-dark-control-base/15'
        ),
      label: (active: boolean) =>
        clsx('select-none transition-colors', !active && 'text-dark-dim'),
      indicator: clsx(
        'h-1.5 w-1.5 rounded-full bg-dark-control-base',
        condition === 'added' && '!bg-green-elevated',
        condition === 'removed' && '!bg-red-elevated'
      ),
    }),
    [condition]
  )

  return (
    <RadioGroup.Root
      className={clsx(
        'flex items-center gap-6 text-xs text-dark-muted',
        className
      )}
      defaultValue="yes"
      value={blockValue ? 'yes' : 'no'}
      aria-label="Yes or no"
      {...props}
    >
      <label className={classes.itemContainer(blockValue)} htmlFor={r1}>
        <RadioGroup.Item
          className={classes.item(blockValue)}
          value="yes"
          id={r1}
        >
          <RadioGroup.Indicator className={classes.indicator} />
        </RadioGroup.Item>
        <span className={classes.label(blockValue)}>Yes</span>
      </label>
      <label className={classes.itemContainer(!blockValue)} htmlFor={r2}>
        <RadioGroup.Item
          className={classes.item(!blockValue)}
          value="no"
          id={r2}
        >
          <RadioGroup.Indicator className={classes.indicator} />
        </RadioGroup.Item>
        <span className={classes.label(!blockValue)}>No</span>
      </label>
    </RadioGroup.Root>
  )
}
